import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { selectActiveContext, useAppSelector } from '@doccareplus/kernel/application';
import { globalSearchApi } from '../infrastructure/globalSearch.api';
import {
  filterAppointmentResults,
  filterStaffResults,
  flattenGroups,
  groupResults,
  mapInvoiceResults,
  mapPatientResults,
  mapVisitResults,
  type SearchGroup,
  type SearchResult,
} from '../domain/searchResult';

/** Minimum characters before any source is queried — avoids one- and two-letter floods. */
export const MIN_QUERY_LENGTH = 2;

/** How far back / forward the appointment window reaches. Kept under the API's 62-day cap. */
const APPT_LOOKBACK_DAYS = 14;
const APPT_LOOKAHEAD_DAYS = 45;
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Query keys for global search. Read keys carry the active `clinicId` so one clinic's cached
 * search rows can never be served to another after a clinic switch.
 */
export const globalSearchKeys = {
  all: ['global-search'] as const,
  patients: (term: string, clinicId?: string) =>
    ['global-search', 'patients', ...(clinicId ? [clinicId] : []), term] as const,
  invoices: (term: string, clinicId?: string) =>
    ['global-search', 'invoices', ...(clinicId ? [clinicId] : []), term] as const,
  staff: (clinicId?: string) => ['global-search', 'staff', ...(clinicId ? [clinicId] : [])] as const,
  appointments: (from: string, to: string, clinicId?: string) =>
    ['global-search', 'appointments', ...(clinicId ? [clinicId] : []), from, to] as const,
  visits: (term: string, clinicId?: string) => ['global-search', 'visits', ...(clinicId ? [clinicId] : []), term] as const,
};

function useActiveClinicId(): string | undefined {
  return useAppSelector(selectActiveContext)?.clinicId;
}

/** A stable-per-mount appointment window anchored to the start of today (UTC). */
function useAppointmentWindow(): { from: string; to: string } {
  return useMemo(() => {
    const now = new Date();
    const startOfToday = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
    return {
      from: new Date(startOfToday - APPT_LOOKBACK_DAYS * DAY_MS).toISOString(),
      to: new Date(startOfToday + APPT_LOOKAHEAD_DAYS * DAY_MS).toISOString(),
    };
  }, []);
}

/**
 * Aggregate global-search results across all sources for `term`, already grouped by category.
 *
 * Patients and invoices are searched server-side; staff and appointments are fetched (staff as
 * the full list, appointments as a bounded window) and filtered client-side. Static navigation
 * commands are merged in by the UI controller, not here.
 */
export function useGlobalSearchResults(term: string): { groups: SearchGroup[]; isFetching: boolean } {
  const clinicId = useActiveClinicId();
  const trimmed = term.trim();
  const enabled = trimmed.length >= MIN_QUERY_LENGTH;
  const window = useAppointmentWindow();

  const patients = useQuery<SearchResult[]>({
    queryKey: globalSearchKeys.patients(trimmed, clinicId),
    queryFn: async () => mapPatientResults(await globalSearchApi.searchPatients(trimmed)),
    enabled,
  });

  const invoices = useQuery<SearchResult[]>({
    queryKey: globalSearchKeys.invoices(trimmed, clinicId),
    queryFn: async () => mapInvoiceResults(await globalSearchApi.searchInvoices(trimmed)),
    enabled,
  });

  // Staff has no server-side search: fetch the full list once (cached per clinic) and filter
  // client-side via `select`, which re-runs as the term changes without re-fetching.
  const staff = useQuery({
    queryKey: globalSearchKeys.staff(clinicId),
    queryFn: () => globalSearchApi.listStaff(),
    enabled,
    staleTime: 5 * 60 * 1000,
    select: (rows) => filterStaffResults(rows, trimmed),
  });

  const appointments = useQuery({
    queryKey: globalSearchKeys.appointments(window.from, window.to, clinicId),
    queryFn: () => globalSearchApi.listAppointments(window.from, window.to),
    enabled,
    staleTime: 60 * 1000,
    select: (rows) => filterAppointmentResults(rows, trimmed),
  });
  const visits = useQuery<SearchResult[]>({ queryKey: globalSearchKeys.visits(trimmed, clinicId), queryFn: async () => mapVisitResults(await globalSearchApi.searchVisits(trimmed)), enabled });

  const groups = useMemo(() => {
    if (!enabled) return [];
    return groupResults([
      ...(patients.data ?? []),
      ...(appointments.data ?? []),
      ...(visits.data ?? []),
      ...(invoices.data ?? []),
      ...(staff.data ?? []),
    ]);
  }, [enabled, patients.data, appointments.data, visits.data, invoices.data, staff.data]);

  return {
    groups,
    isFetching:
      enabled &&
      (patients.isFetching || invoices.isFetching || staff.isFetching || appointments.isFetching || visits.isFetching),
  };
}

export { flattenGroups };
